import React, { useEffect, useState, useContext } from 'react';
import Select from 'react-select';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from "../UserContext";
import axios from 'axios';
import Header from '../Header'; // assuming you have a shared Header component
import './index.css';   // style this file for table responsiveness

const CustomersList = () => {
  const { user, token } = useContext(UserContext);
  const [customers, setCustomers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [selectedOperatorId, setSelectedOperatorId] = useState('all');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showLoading, setShowLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); 
// values like: all, vr_active, vr_inactive, ott1_active, etc.

  // Which expiry column is currently sorted, and in which direction.
  // field matches the customer object's own key for that provider's expiry.
  const [sortConfig, setSortConfig] = useState({ field: 'ExpiryDate', asc: false });
  const [visibleColumns, setVisibleColumns] = useState({});

  const navigate = useNavigate();
  
  const role = user.role;

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setShowLoading(true);
      const response = await axios.get(`https://sitiplay.com/portal_php/allCustomers.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });      
      setCustomers(response.data || []);
      setFilteredCustomers(response.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load customers');
      setCustomers([]);
    } finally {
      setLoading(false);
      setShowLoading(false);
    }
  };

  const fetchOperators = async () => {
      try {
        const response = await fetch(`https://sitiplay.com/portal_php/operator_api.php`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        const data = await response.json();        
        if (response.ok) {
          setOperators(data.operators);
          setError(null);
        } else {
          setError(data.error || 'Failed to fetch operators');
        }
      } catch (err) {
        setError('Network error');
      } finally {
        setLoading(false);
      }
    };

    const fetchVisibleColumns =
      async () => {
        try {

          const res =
            await fetch(
              "https://sitiplay.com/portal_php/customer/get_operator_visible_packages.php",
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`
                }
              }
            );

          const data =
            await res.json();

          if (data.success) {
            setVisibleColumns(
              data.packages
            );
          }

        } catch (err) {
          console.error(err);
        }
    };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (role !== "operator") {
      fetchOperators();
    }
  }, [role]);

  useEffect(() => {
    if (token) {
      fetchVisibleColumns();
    }
  }, [token]);

  // Debounced Search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (!searchTerm) {
        setFilteredCustomers(customers);
        return;
      }
      const term = searchTerm.toLowerCase();
      const results = customers.filter(
        (c) =>
          (c.name && c.name.toLowerCase().includes(term)) ||
          (c.MobileNumber && c.MobileNumber.includes(term)) ||
          (c.username && c.username.toLowerCase().includes(term))
      );
      setFilteredCustomers(results);
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, customers]);

  const isAnyPlanActive = (cust) => {
    const today = new Date();

    const dates = [
      cust.ExpiryDate,
      cust.playboxtv_expiry,
      cust.watcho_expiry,
      cust.antiptv_expiry,
      cust.pioneeriptv_expiry,
      cust.etvwin_expiry
    ];

    return dates.some(date => {
      if (!date || date === "0000-00-00") return false;
      return new Date(date) >= today;
    });
  };

  useEffect(() => {
    let list = [...customers];

    if (filterStatus === 'vr_active') {
      list = list.filter(isVRActive);
    } else if (filterStatus === 'vr_inactive') {
      list = list.filter(c => !isVRActive(c));
    } else if (filterStatus === 'ott2_active') {
      list = list.filter(isPlayboxtvActive);
    } else if (filterStatus === 'ott2_inactive') {
      list = list.filter(c => !isPlayboxtvActive(c));
    } else if (filterStatus === 'watcho_active') {
      list = list.filter(isWatchoActive);
    } else if (filterStatus === 'watcho_inactive') {
      list = list.filter(c => !isWatchoActive(c));
    // } else if (filterStatus === 'antiptv_active') {
    //   list = list.filter(isAntiptvActive);
    // } else if (filterStatus === 'antiptv_inactive') {
    //   list = list.filter(c => !isAntiptvActive(c));
    } else if (filterStatus === 'pioneeriptv_active') {
      list = list.filter(isPioneerIptvActive);
    } else if (filterStatus === 'pioneeriptv_inactive') {
      list = list.filter(c => !isPioneerIptvActive(c));
    } else if (filterStatus === 'etvwin_active') {
      list = list.filter(isEtvwinActive);
    } else if (filterStatus === 'etvwin_inactive') {
      list = list.filter(c => !isEtvwinActive(c));
    }

    if (selectedOperatorId !== 'all') {
      list = list.filter(c => c.operatorId === selectedOperatorId);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (c) =>
          (c.name && c.name.toLowerCase().includes(term)) ||
          (c.MobileNumber && c.MobileNumber.includes(term)) ||
          (c.username && c.username.toLowerCase().includes(term))
      );
    }

    // Re-apply whichever column is currently sorted so filtering doesn't
    // reset the table back to its unsorted order.
    list.sort((a, b) => {
      const dateA = new Date(a[sortConfig.field]);
      const dateB = new Date(b[sortConfig.field]);
      return sortConfig.asc ? dateA - dateB : dateB - dateA;
    });

    setFilteredCustomers(list);
  }, [searchTerm, customers, filterStatus, selectedOperatorId, sortConfig]);

  // Clicking a column header sorts by THAT column's expiry field.
  // Clicking the same header again flips direction; clicking a different
  // header switches to that field, starting descending.
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      asc: prev.field === field ? !prev.asc : false
    }));
  };

  const sortIndicator = (field) => {
    if (sortConfig.field !== field) return '';
    return sortConfig.asc ? ' ▲' : ' ▼';
  };

  const operatorOptions = [
    { value: 'all', label: 'All Operators' },

    ...(role === "admin"
      ? [{
          value: user.operatorId,
          label: `${user.phoneNumber || user.operatorPhoneNumber || ''} - ${user.operatorName} (Admin)`
        }]
      : []),

    ...operators.map(op => ({
      value: op.id,
      label: `${op.phoneNumber || ''} - ${op.operatorName}`
    }))
  ];

  const highlightText = (text, term) => {
    if (!term) return text;
    const regex = new RegExp(`(${term})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  };

  const formatExpiry = (date) => {
    if (!date || date === "0000-00-00") return "N/A";
    return date;
  };

  const isPlanActive = (date) => {
    if (!date || date === "0000-00-00") return false;
    return new Date(date) >= new Date();
  };

  const isVRActive = (c) => isPlanActive(c.ExpiryDate);
  const isPlayboxtvActive = (c) => isPlanActive(c.playboxtv_expiry);
  const isWatchoActive = (c) => isPlanActive(c.watcho_expiry);
  const isAntiptvActive = (c) => isPlanActive(c.antiptv_expiry);
  const isPioneerIptvActive = (c) => isPlanActive(c.pioneeriptv_expiry);
  const isEtvwinActive = (c) => isPlanActive(c.etvwin_expiry);

  return (
    <>
      <Header />

      <div className="operator-header">
        <h2>Customers</h2>
        {role !== "operator" && (
          <Select
            options={operatorOptions}
            value={
              operatorOptions.find(op => op.value === selectedOperatorId) 
              || operatorOptions[0]
            }
            onChange={(selected) =>
              setSelectedOperatorId(
                selected.value === 'all' ? 'all' : Number(selected.value)
              )
            }
            placeholder="Search by phone or name..."
            isSearchable
            styles={{
              container: (base) => ({
                ...base,
                minWidth: 250,   // ✅ FIXED WIDTH
                width: 250       // optional fixed width
              })
            }}
          />
        )}

        <input
          type="text"
          className="search-input"
          placeholder="Search by name or phone"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button className="add-operator-btn" onClick={() => navigate('/add-customer')}>
          + Add Customer
        </button>
      </div>


      <div className="customer-summary">
        <div className="customer-summary">
          <strong>Total: {customers.length}</strong> |{" "}
          <span>SITIPLAY: {customers.filter(isVRActive).length}</span> |{" "}
          {/* <span>Antiptv: {customers.filter(isAntiptvActive).length}</span> |{" "} */}
          <span>Watcho: {customers.filter(isWatchoActive).length}</span> |{" "}
          <span>PioneerIptv: {customers.filter(isPioneerIptvActive).length}</span> |{" "}
          <span>Etvwin: {customers.filter(isEtvwinActive).length}</span>
        </div>
        <div className="filter-section">
          <label>Filter by Status:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="search-input"
          >
            <option value="all">All</option>
            <option value="vr_active">SITIPLAY Active</option>
            <option value="vr_inactive">SITIPLAY Inactive</option>
            {visibleColumns.watcho && (
              <>
                <option value="watcho_active">Watcho Active</option>
                <option value="watcho_inactive">Watcho Inactive</option>
              </>
            )}
            {/* {visibleColumns.playboxtv && (
              <>
                <option value="ott2_active">Playbox TV Active</option>
                <option value="ott2_inactive">Playbox TV Inactive</option>
              </>
            )} */}
            {/* {visibleColumns.antiptv && (
              <>
                <option value="antiptv_active">Antiptv Active</option>
                <option value="antiptv_inactive">Antiptv Inactive</option>
              </>
            )} */}
            {visibleColumns.pioneeriptv && (
              <>
                <option value="pioneeriptv_active">PioneerIptv Active</option>
                <option value="pioneeriptv_inactive">PioneerIptv Inactive</option>
              </>
            )}
            {visibleColumns.etvwin && (
              <>
                <option value="etvwin_active">Etvwin Active</option>
                <option value="etvwin_inactive">Etvwin Inactive</option>
              </>
            )}
          </select>
        </div>
      </div>

        {showLoading || loading ? (
        <div className="loading-container">
          <div className="skeleton-loader">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="skeleton-row">
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
                <div className="skeleton-cell"></div>
              </div>
            ))}
          </div>
        </div>
      ) : error ? (
          <p className="error">{error}</p>
        ) : (
          <div className="operator-list table-wrapper">
            {filteredCustomers.length > 0 ? (
              <table className="customers-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Username</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th onClick={() => handleSort('ExpiryDate')} style={{ cursor: 'pointer' }}>
                      SITIPLAY Expiry{sortIndicator('ExpiryDate')}
                    </th>
                    {visibleColumns.etvwin && (
                    <th onClick={() => handleSort('etvwin_expiry')} style={{ cursor: 'pointer' }}>
                      Etvwin Expiry{sortIndicator('etvwin_expiry')}
                    </th>
                    )}
                    {visibleColumns.watcho && (
                    <th onClick={() => handleSort('watcho_expiry')} style={{ cursor: 'pointer' }}>
                      Watcho Expiry{sortIndicator('watcho_expiry')}
                    </th>
                    )}
                    {/* {visibleColumns.playboxtv && (
                    <th onClick={() => handleSort('playboxtv_expiry')} style={{ cursor: 'pointer' }}>
                      Playbox TV Expiry{sortIndicator('playboxtv_expiry')}
                    </th>
                    )} */}
                    {visibleColumns.pioneeriptv && (
                    <th onClick={() => handleSort('pioneeriptv_expiry')} style={{ cursor: 'pointer' }}>
                      PioneerIptv Expiry{sortIndicator('pioneeriptv_expiry')}
                    </th>
                    )}
                    {/* {visibleColumns.antiptv && (
                    <th onClick={() => handleSort('antiptv_expiry')} style={{ cursor: 'pointer' }}>
                      Antiptv Expiry{sortIndicator('antiptv_expiry')}
                    </th>
                    )} */}
                    {user.role !== "operator" && <th>Operator</th>}
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((cust, index) => (
                    <tr key={cust.id} className={isAnyPlanActive(cust) ? 'row-active' : 'row-inactive'}>
                      <td>{index + 1}</td>
                      <td>
                        <Link to={`/customer/${cust.id}`}>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: highlightText(cust.username, searchTerm),
                            }}
                          />
                        </Link>
                      </td>

                      <td>
                        <Link to={`/customer/${cust.id}`}>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: highlightText(cust.name + " " + cust.lastName, searchTerm),
                            }}
                          />
                        </Link>
                      </td>
                      <td>{cust.MobileNumber}</td>
                      <td className={isPlanActive(cust.ExpiryDate) ? 'row-active' : 'row-inactive'}>{formatExpiry(cust.ExpiryDate)}</td>
                      {visibleColumns.etvwin && (
                        <td className={isPlanActive(cust.etvwin_expiry) ? 'row-active' : 'row-inactive'}>{formatExpiry(cust.etvwin_expiry)}</td>
                      )}
                      {visibleColumns.watcho && (
                        <td className={isPlanActive(cust.watcho_expiry) ? 'row-active' : 'row-inactive'}>{formatExpiry(cust.watcho_expiry)}</td>
                      )}
                      {/* {visibleColumns.playboxtv && (
                        <td className={isPlanActive(cust.playboxtv_expiry) ? 'row-active' : 'row-inactive'}>{formatExpiry(cust.playboxtv_expiry)}</td>
                      )} */}
                      {visibleColumns.pioneeriptv && (
                        <td className={isPlanActive(cust.pioneeriptv_expiry) ? 'row-active' : 'row-inactive'}>{formatExpiry(cust.pioneeriptv_expiry)}</td>
                      )}
                      {/* {visibleColumns.antiptv && (
                        <td className={isPlanActive(cust.antiptv_expiry) ? 'row-active' : 'row-inactive'}>{formatExpiry(cust.antiptv_expiry)}</td>
                      )} */}
                      {user.role !== "operator" && <td>{cust.operatorDetails}</td>}
                      <td>{cust.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-data">No customers found.</p>
            )}
          </div>
        )}
    </>
  );
};

export default CustomersList;